import { Label } from '@creanizer/ui'
import { Input } from '@creanizer/ui'
import { Textarea } from '@creanizer/ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@creanizer/ui'
import { Slider } from '@creanizer/ui'
import { generateEnemyContext, generateItemContext, generateCharacterContext, generateCommunityContext, generateSocietyContext } from '@creanizer/utils'
import type { DungeonInput } from '@creanizer/types'

interface DungeonFormStandardProps {
  input: DungeonInput
  onChange: (input: DungeonInput) => void
  enemies: any[]
  items: any[]
  characters: any[]
  communities: any[]
  societies: any[]
  selectedEnemyId: string
  setSelectedEnemyId: (id: string) => void
  selectedItemId: string
  setSelectedItemId: (id: string) => void
  selectedCharacterId: string
  setSelectedCharacterId: (id: string) => void
  selectedCommunityId: string
  setSelectedCommunityId: (id: string) => void
  selectedSocietyId: string
  setSelectedSocietyId: (id: string) => void
}

export function DungeonFormStandard({
  input,
  onChange,
  enemies,
  items,
  characters,
  communities,
  societies,
  selectedEnemyId,
  setSelectedEnemyId,
  selectedItemId,
  setSelectedItemId,
  selectedCharacterId,
  setSelectedCharacterId,
  selectedCommunityId,
  setSelectedCommunityId,
  selectedSocietyId,
  setSelectedSocietyId
}: DungeonFormStandardProps) {
  return (
    <div className="space-y-4">
      <div>
        <Label>ダンジョン名（オプション）</Label>
        <Input
          value={input.name || ''}
          onChange={(e) => onChange({ ...input, name: e.target.value })}
          placeholder="例：忘れられた神殿"
        />
      </div>
      
      <div>
        <Label>ダンジョンタイプ</Label>
        <Input
          value={input.type || ''}
          onChange={(e) => onChange({ ...input, type: e.target.value })}
          placeholder="例：古代遺跡、洞窟、要塞"
        />
      </div>
      
      <div>
        <Label>テーマ</Label>
        <Input
          value={input.theme || ''}
          onChange={(e) => onChange({ ...input, theme: e.target.value })}
          placeholder="例：失われた文明、魔法の迷宮"
        />
      </div>
      
      <div>
        <Label>フロア数: {input.floors || 3}</Label>
        <Slider
          value={[input.floors || 3]}
          onValueChange={(value) => onChange({ ...input, floors: value[0] })}
          min={1}
          max={10}
          step={1}
        />
      </div>
      
      <div>
        <Label>部屋数（フロアあたり）: {input.roomsPerFloor || 5}</Label>
        <Slider
          value={[input.roomsPerFloor || 5]}
          onValueChange={(value) => onChange({ ...input, roomsPerFloor: value[0] })}
          min={1}
          max={20}
          step={1}
        />
      </div>
      
      <div>
        <Label>レイアウト</Label>
        <Select 
          value={input.layout} 
          onValueChange={(value) => onChange({ ...input, layout: value as DungeonInput['layout'] })}
        >
          <SelectTrigger>
            <SelectValue placeholder="レイアウトを選択" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="linear">直線的</SelectItem>
            <SelectItem value="branching">分岐</SelectItem>
            <SelectItem value="maze">迷路</SelectItem>
            <SelectItem value="open">開放的</SelectItem>
            <SelectItem value="custom">カスタム</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      <div>
        <Label>規模</Label>
        <Select 
          value={input.size} 
          onValueChange={(value) => onChange({ ...input, size: value as DungeonInput['size'] })}
        >
          <SelectTrigger>
            <SelectValue placeholder="規模を選択" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="small">小規模</SelectItem>
            <SelectItem value="medium">中規模</SelectItem>
            <SelectItem value="large">大規模</SelectItem>
            <SelectItem value="massive">超大規模</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      {/* コンテンツ密度設定 */}
      <div>
        <Label>トラップ密度</Label>
        <Select 
          value={input.trapDensity} 
          onValueChange={(value) => onChange({ ...input, trapDensity: value as DungeonInput['trapDensity'] })}
        >
          <SelectTrigger>
            <SelectValue placeholder="トラップ密度を選択" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">なし</SelectItem>
            <SelectItem value="low">低</SelectItem>
            <SelectItem value="medium">中</SelectItem>
            <SelectItem value="high">高</SelectItem>
            <SelectItem value="extreme">極限</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      <div>
        <Label>敵密度</Label>
        <Select 
          value={input.enemyDensity} 
          onValueChange={(value) => onChange({ ...input, enemyDensity: value as DungeonInput['enemyDensity'] })}
        >
          <SelectTrigger>
            <SelectValue placeholder="敵密度を選択" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">なし</SelectItem>
            <SelectItem value="low">低</SelectItem>
            <SelectItem value="medium">中</SelectItem>
            <SelectItem value="high">高</SelectItem>
            <SelectItem value="extreme">極限</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      <div>
        <Label>宝物の豊富さ</Label>
        <Select 
          value={input.treasureRichness} 
          onValueChange={(value) => onChange({ ...input, treasureRichness: value as DungeonInput['treasureRichness'] })}
        >
          <SelectTrigger>
            <SelectValue placeholder="宝物の豊富さを選択" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="poor">貧弱</SelectItem>
            <SelectItem value="modest">控えめ</SelectItem>
            <SelectItem value="average">平均的</SelectItem>
            <SelectItem value="rich">豊富</SelectItem>
            <SelectItem value="legendary">伝説的</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      <div>
        <Label>パズル複雑度</Label>
        <Select 
          value={input.puzzleComplexity} 
          onValueChange={(value) => onChange({ ...input, puzzleComplexity: value as DungeonInput['puzzleComplexity'] })}
        >
          <SelectTrigger>
            <SelectValue placeholder="パズル複雑度を選択" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">なし</SelectItem>
            <SelectItem value="simple">簡単</SelectItem>
            <SelectItem value="moderate">適度</SelectItem>
            <SelectItem value="complex">複雑</SelectItem>
            <SelectItem value="genius">天才的</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      {/* エネミー連携 */}
      {enemies.length > 0 && (
        <div className="space-y-2">
          <Label>配置エネミー（オプション）</Label>
          <Select
            value={selectedEnemyId || 'none'}
            onValueChange={(value) => {
              if (value === 'none') {
                setSelectedEnemyId('')
                onChange({ 
                  ...input, 
                  relatedEnemyContext: undefined,
                  enemyRole: undefined 
                })
              } else {
                const enemy = enemies.find(e => e.id === value)
                if (enemy) {
                  const context = generateEnemyContext(enemy)
                  onChange({ 
                    ...input, 
                    relatedEnemyContext: context 
                  })
                  setSelectedEnemyId(value)
                }
              }
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="エネミーを選択..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">なし</SelectItem>
              {enemies.map((enemy) => (
                <SelectItem key={enemy.id} value={enemy.id}>
                  {enemy.result.basicInfo?.name || enemy.result.name || 'Unknown'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {selectedEnemyId && (
            <div className="mt-2">
              <Label>エネミーの役割（任意）</Label>
              <Input
                value={input.enemyRole || ''}
                onChange={(e) => onChange({ 
                  ...input, 
                  enemyRole: e.target.value 
                })}
                placeholder="例：ボス、中ボス、雑魚、守護者等"
              />
            </div>
          )}
        </div>
      )}
      
      {/* アイテム連携 */}
      {items.length > 0 && (
        <div className="space-y-2">
          <Label>配置アイテム（オプション）</Label>
          <Select
            value={selectedItemId || 'none'}
            onValueChange={(value) => {
              if (value === 'none') {
                setSelectedItemId('')
                onChange({ 
                  ...input, 
                  relatedItemContext: undefined,
                  itemPlacement: undefined 
                })
              } else {
                const item = items.find(i => i.id === value)
                if (item) {
                  const context = generateItemContext(item)
                  onChange({ 
                    ...input, 
                    relatedItemContext: context 
                  })
                  setSelectedItemId(value)
                }
              }
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="アイテムを選択..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">なし</SelectItem>
              {items.map((item) => (
                <SelectItem key={item.id} value={item.id}>
                  {item.result.basicInfo?.name || item.result.name || 'Unknown'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {selectedItemId && (
            <div className="mt-2">
              <Label>配置方法（任意）</Label>
              <Input
                value={input.itemPlacement || ''}
                onChange={(e) => onChange({ 
                  ...input, 
                  itemPlacement: e.target.value 
                })}
                placeholder="例：宝箱、隠し部屋、ボスドロップ等"
              />
            </div>
          )}
        </div>
      )}
      
      {/* キャラクター連携 */}
      {characters.length > 0 && (
        <div className="space-y-2">
          <Label>関連キャラクター（オプション）</Label>
          <Select
            value={selectedCharacterId || 'none'}
            onValueChange={(value) => {
              if (value === 'none') {
                setSelectedCharacterId('')
                onChange({ 
                  ...input, 
                  relatedCharacterContext: undefined,
                  characterRole: undefined 
                })
              } else {
                const character = characters.find(c => c.id === value)
                if (character) {
                  const context = generateCharacterContext(character)
                  onChange({ 
                    ...input, 
                    relatedCharacterContext: context 
                  })
                  setSelectedCharacterId(value)
                }
              }
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="キャラクターを選択..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">なし</SelectItem>
              {characters.map((character) => (
                <SelectItem key={character.id} value={character.id}>
                  {character.result.name} ({character.result.role})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {selectedCharacterId && (
            <div className="mt-2">
              <Label>キャラクターの役割（任意）</Label>
              <Input
                value={input.characterRole || ''}
                onChange={(e) => onChange({ 
                  ...input, 
                  characterRole: e.target.value 
                })}
                placeholder="例：探索者、案内人、囚われの人等"
              />
            </div>
          )}
        </div>
      )}
      
      {/* 追加の要望 */}
      <div>
        <Label>追加の要望（オプション）</Label>
        <Textarea
          value={input.additionalRequest || ''}
          onChange={(e) => onChange({ ...input, additionalRequest: e.target.value })}
          placeholder="例：巨大な図書館がある、水没したエリアがある、時間制限のギミックがある、特定のモンスターを配置したい等、自由に要望を記入してください"
          className="min-h-[100px]"
        />
        <p className="text-xs text-muted-foreground mt-1">
          ダンジョンに関する特別な要望や設定を自由に記入できます
        </p>
      </div>
    </div>
  )
}